import { useEffect, useState, useRef } from "react";
import {
    Card,
    CardBody,
    CardFooter,
    Button,
    Typography,
} from "@material-tailwind/react";
import { Form, Formik } from "formik";
import FormikInputIcon from "components/form/formik-input-icon";
import * as Yup from "yup";
import { useNavigate, Link } from "react-router";
import Image from "components/image";
import logo from "assets/img/logos/gobby-anim.gif";
import { toast } from "react-toastify";
import { apiFetch } from "utils/api";
import { useRateLimit, sanitiseInput } from "utils/utils.jsx";

export default function ResetPassword() {
    const navigate = useNavigate();
    const emailInputRef = useRef(null);
    const { isBlocked: resetBlocked } = useRateLimit();
    const [submitDisabled, setSubmitDisabled] = useState(false);

    const initialEmailValues = { email: "" };

    const emailValidationSchema = Yup.object().shape({
        email: Yup.string()
            .email("Please enter a valid email address")
            .required("Email is required"),
    });

    const handleEmailSubmit = async (values, { setSubmitting }) => {
        if (resetBlocked || submitDisabled) {
            console.warn('handleEmailSubmit: Rate limit exceeded.');
            toast.error('Too many attempts. Please try again later.');
            setSubmitting(false);
            return;
        }
        setSubmitDisabled(true);
        setSubmitting(true);
        const sanitisedEmail = encodeURIComponent(sanitiseInput(values.email));
        const sanitisedUrl = encodeURIComponent(sanitiseInput(window.location.origin + '/resetconfirm?token='));
        console.log('handleEmailSubmit: Attempting password reset for email:', sanitisedEmail);
        try {
            if (!sanitisedUrl || !sanitisedEmail) {
                throw new Error('Invalid input: missing URL or email.');
            }
            const endpoint = `/v2/password-reset?url=${sanitisedUrl}&email=${sanitisedEmail}`;
            const response = await apiFetch(endpoint, {method:'GET'});
            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText || 'Unknown error'}`;
                try {
                    const errorText = await response.text();
                    if (errorText) {
                        errorMessage = errorText.trim();
                    }
                } catch (textError) {
                    console.warn('handleEmailSubmit: Failed to read error response text:', textError.message);
                }
                console.error('handleEmailSubmit: Error response:', { status: response.status, message: errorMessage });
                throw new Error(errorMessage);
            }

            // Handle 204 No Content or empty responses
            if (response.status === 204) {
                console.log('handleEmailSubmit: Success with empty response.');
                toast.success('Password reset link sent to your email.');
                return;
            }
        } catch (error) {
            console.error('handleEmailSubmit: Password reset request failed:', error.message);
            console.error('handleEmailSubmit: Error details:', error);
            toast.error('Failed to send reset link. Please try again.');
        } finally {
            setSubmitting(false);
            setTimeout(() => setSubmitDisabled(false), 5000);
        }
    };

    // Auto-focus on email input
    useEffect(() => {
        if (emailInputRef.current) {
            emailInputRef.current.focus();
        }
    }, []);

    return (
        <>
            <div className="flex flex-col min-w-screen min-h-screen bg-white">
                <div className="basis-0 h-[250px] md:basis-1/3 pl-3 pt-3">
                    <Image
                        className="sm:w-[250px] w-[0px]"
                        src={logo}
                        rounded={false}
                        raised={false}
                        alt="Gobby Logo"
                    />
                </div>
                <div className="flex min-h-fit justify-center">
                    <Card
                        className="card-inner filter-none sm:p-8 p-4"
                        shadow={false}
                    >
                        <Typography className="font-display text-center md:text-xl lg:content-center md:mt-2 text-primary">
                            Forgot Your Password?
                        </Typography>
                        <Formik
                            initialValues={initialEmailValues}
                            onSubmit={handleEmailSubmit}
                            validationSchema={emailValidationSchema}
                        >
                            {({ isValid, dirty, isSubmitting }) => (
                                <Form>
                                    <CardBody className="card-inner-body font-body my-4 py-2">
                                        <div className="my-4 px-0">
                                            <Typography
                                                as="div"
                                                className="px-4 rounded-2xl"
                                            >
                                                <FormikInputIcon
                                                    name="email"
                                                    iconName="email"
                                                    label="Registered Email Address"
                                                    type="email"
                                                    aria-label="Enter your registered email address"
                                                    ref={emailInputRef}
                                                />
                                            </Typography>
                                        </div>
                                    </CardBody>
                                    <CardFooter className="p-0">
                                        <div className="flex justify-center my-4">
                                            <Button
                                                type="submit"
                                                variant="filled"
                                                size="lg"
                                                className="btn btn--cta font-display text-2xl flex items-center justify-center"
                                                disabled={!(isValid && dirty) || isSubmitting || submitDisabled}
                                            >
                                                {isSubmitting ? 'Sending...' : 'Submit'}
                                            </Button>
                                        </div>
                                        <Typography variant="small" className="text-center text-black mb-4">
                                            Please allow a minimum of 5 minutes to receive your email, before trying again.
                                        </Typography>
                                        <Link
                                            className="w-full text-center"
                                            to="/login"
                                        >
                                            <Typography
                                                className="my-2 btn btn--primary text-white"
                                                as="span"
                                            >
                                                Back to Login
                                            </Typography>
                                        </Link>
                                    </CardFooter>
                                </Form>
                            )}
                        </Formik>
                    </Card>
                </div>
            </div>
        </>
    );
}
